import { useCallback, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { useShow } from "@/contexts/ShowContext";
import { Scene3DEditor } from "@/components/Scene3DEditor";

// The 3D scene document (Scene3DEditor's own concern) and the Asset that
// wraps it for the assets grid (name/thumbnail/scene3d pointer) are two
// separate documents — this mirrors what AssetsEditor used to do in
// handleScene3DSaveComplete back when this editor was an in-place overlay
// there, now driven by the folder/assetId this route was opened with (see
// AssetsEditor's buildScene3DPath) instead of local state.
export default function Scene3DPage() {
  const { sceneId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { show } = useShow();

  const folderId = searchParams.get("folder") || null;
  // Local, not URL-synced: same as the old editingAssetId component state —
  // only needed so a second save (without leaving the page) updates this
  // same asset instead of creating a new one each time.
  const [assetId, setAssetId] = useState(searchParams.get("assetId") || null);

  const handleClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Mirrors AssetsEditor's buildScene3DPath. Uses react-router's own
  // navigate(path, {replace:true}) rather than a raw history.replaceState —
  // an earlier version hand-rolled the browser History API directly to
  // dodge Scene3DEditor's load effect, but react-router's compiled route
  // renderer (_renderMatches in its own source) gives the rendered route no
  // distinguishing `key`, so switching from the "scene3d/new" match to the
  // "scene3d/:sceneId" match here does NOT remount Scene3DPage/Scene3DEditor
  // — it only changes the `sceneId` prop they already handle. That makes a
  // real navigate() safe, and safer than reinventing react-router's own
  // index/key bookkeeping by hand: a hand-rolled replaceState call that
  // preserved `history.state` fixed the Back button "forgetting" this entry
  // once, but still left Back landing on the wrong entry afterwards — using
  // the library's own tested replace avoids relitigating that bookkeeping at
  // all. The remaining risk (Scene3DEditor's load effect re-firing on the
  // sceneId change and refetching over in-progress edits) is handled at its
  // source instead — see the `sceneId === dbSceneId` guard in
  // Scene3DEditor.jsx's load effect.
  const updateUrlForSavedScene = useCallback(
    (savedSceneId, resolvedAssetId) => {
      const params = new URLSearchParams();
      if (folderId) params.set("folder", folderId);
      if (resolvedAssetId) params.set("assetId", resolvedAssetId);
      const qs = params.toString();
      const newPath = `/show/scene3d/${savedSceneId}${qs ? `?${qs}` : ""}`;
      navigate(newPath, { replace: true });
    },
    [folderId, navigate],
  );

  const handleSaveComplete = useCallback(
    async ({ sceneId: savedSceneId, imageUrl, imageKey, name }) => {
      try {
        let resolvedAssetId = assetId;
        if (assetId) {
          await apiFetch(`/api/shows/${show._id}/assets/${assetId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name,
              url: imageUrl,
              key: imageKey,
              scene3d: savedSceneId,
              thumbnail_url: imageUrl,
              thumbnail_key: imageKey,
            }),
          });
        } else {
          const res = await apiFetch(`/api/shows/${show._id}/assets`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              mediaType: "image",
              name,
              url: imageUrl,
              key: imageKey,
              thumbnail_url: imageUrl,
              thumbnail_key: imageKey,
              source: "3d-scene",
              scene3d: savedSceneId,
              folder: folderId,
            }),
          });
          const created = await res.json();
          if (created?._id) {
            resolvedAssetId = created._id;
            setAssetId(created._id);
          }
        }
        updateUrlForSavedScene(savedSceneId, resolvedAssetId);
        toast.success("Asset saved");
      } catch (error) {
        console.error("Error saving 3D scene asset:", error);
        toast.error("Failed to save asset");
      }
    },
    [assetId, show._id, folderId, updateUrlForSavedScene],
  );

  return (
    <Scene3DEditor
      sceneId={sceneId || null}
      folderId={folderId}
      onClose={handleClose}
      onSaveComplete={handleSaveComplete}
    />
  );
}
